import { cn, Flex } from '@bridebook/aura';
import { isDateExact } from '@bridebook/toolbox';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { cva } from 'class-variance-authority';
import {
  DATE_FORMAT,
  dateFromExactDate,
  format,
} from '@bridebook/web/lib/time-date-manipulation/time-date-manipulation';
import { PublicUrls } from '@bridebook/web/lib/url-helper';
import { useDispatch } from '@bridebook/web/lib/utils';
import { interactedWithWeddingWebsiteAnalyticsButtonClick } from '@bridebook/web/lib/wedding-website/analytics';
import {
  useGetWeddingWebsiteDateFormat,
  WeddingWebsiteDateFormat,
} from '@bridebook/web/lib/wedding-website/hooks/use-get-wedding-website-date-format';
import { isVenueSection } from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import { compact } from 'lodash';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { MouseEventHandler, PropsWithChildren, useCallback, useMemo } from 'react';
import { Button } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/button';
import { SectionContainer } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/section-container';
import {
  Header1,
  Header3,
  SubheaderText,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/typography';
import { useWeddingWebsiteData } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/data/data-context';
import { useIsEditMode } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/data/data-provider-panel';
import { Palette } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/palette-config';
import { useThemeContext } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/theme-provider';
import { MobileAccent } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/sections/wedding-overview/mobile-accent';
import { toMainPalette } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/utils';

const getNamesMaxWidthClassName = (palette: Palette) => {
  switch (palette) {
    case Palette.Amore:
      return 'max-w-[calc(100%-60px)] @screen-lg:max-w-[calc(100%-60px)]';
    default:
      return '';
  }
};
const detailsWrapperVariants = cva('', {
  variants: {
    palette: {
      [Palette.Bridebook]: 'max-w-full',
      [Palette.Roses]: 'max-w-full',
      [Palette.Foliage]: 'max-w-[calc(100%-150px)]',
      [Palette.Whimsical]: 'max-w-[calc(100%-180px)]',
      [Palette.Sketches]: 'max-w-[calc(100%-140px)]',
      [Palette.Glamour]: '',
      [Palette.Timeless]: '',
      [Palette.Blossom]: '',
      [Palette.Amore]: 'max-w-[calc(100%-60px)]',
    },
  },
});

export const WeddingOverview = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation('weddingWebsite');
  const { wedding, website, suppliers } = useWeddingWebsiteData();
  const { palette } = useThemeContext();

  // names
  const missingPartnerName = t('partners.missingNo');
  const [name, partnerName] = wedding.partners;
  const names = [name || missingPartnerName, partnerName || missingPartnerName].join(' & ');

  // venue
  const venueSection = website.sections.find(isVenueSection);
  const mainSupplier = venueSection
    ? suppliers.find((supplier) => supplier.id === venueSection.data.supplier)
    : undefined;

  const formattedAddress = mainSupplier
    ? compact([mainSupplier.name, mainSupplier.address?.city, mainSupplier.address?.country]).join(
        ', ',
      )
    : undefined;

  // date
  const dateFormat = useGetWeddingWebsiteDateFormat();

  const formattedDate = useMemo(() => {
    if (!isDateExact(wedding.date)) return undefined;
    return format(
      dateFromExactDate(wedding.date),
      dateFormat === WeddingWebsiteDateFormat.Ordinal
        ? DATE_FORMAT.DATE.CALENDAR.LONGEST
        : DATE_FORMAT.DATE.WITH_DAY,
    );
  }, [wedding.date, dateFormat]);

  // rsvp button
  const market = useMarket();
  const websiteId = website.id ?? '';
  const router = useRouter();
  const guestNameFromQuery = router.query.guest as string | undefined;
  const guestIdFromQuery = router.query.guestId as string | undefined;

  const dispatch = useDispatch();
  const editMode = useIsEditMode();
  const isPublished = website.privacy?.published;

  const handleRSVPClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      if (editMode || !isPublished) {
        if (!isPublished) e.preventDefault();
        return;
      }
      dispatch(
        interactedWithWeddingWebsiteAnalyticsButtonClick({
          section: 'rsvp',
          element: 'RSVP',
        }),
      );
    },
    [dispatch, editMode, isPublished],
  );

  return (
    <div className="relative flex w-full flex-col items-center">
      {/* Accents are positioned relative to the wedding overview section */}
      <MobileAccent />
      {children}
      <SectionContainer
        className="relative z-20 px-4 @screen-lg:px-10"
        detailsSectionId="overview"
        previewSectionId="overview">
        <Header1
          className={cn(
            'max-w-[280px] break-words [word-break:break-word] @screen-lg:max-w-none',
            getNamesMaxWidthClassName(palette),
          )}>
          {names}
        </Header1>

        <Flex
          className={cn(
            'w-full flex-col items-center gap-6',
            detailsWrapperVariants({ palette: toMainPalette(palette) }),
          )}>
          <Header3>{formattedDate ?? t('previewMode.tile.overview.dateToBeAnnounced')}</Header3>
          <SubheaderText>
            {formattedAddress ?? t('previewMode.tile.overview.venueToBeAnnounced')}
          </SubheaderText>
        </Flex>

        {website.config.rsvpVisible && (
          <Button asChild>
            <a
              aria-disabled={!isPublished}
              href={`/${market.prefix}${PublicUrls.for.rsvp(websiteId, { guest: guestNameFromQuery, guestId: guestIdFromQuery })}`}
              target="_blank"
              rel="noreferrer"
              onClick={handleRSVPClick}>
              {t('previewMode.tile.overview.cta')}
            </a>
          </Button>
        )}
      </SectionContainer>
    </div>
  );
};
